import { Component } from 'react';

/**
 * Without this, one thrown render error unmounts the whole tree and the visitor
 * gets a blank white page with no explanation and no way forward.
 *
 * Still a class: `componentDidCatch` has no hook equivalent in React 19.
 */
export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    /*
     * Where an error-tracking service would be wired in. Until then this at
     * least puts the component stack somewhere a developer can find it, rather
     * than losing it with the unmounted tree.
     */
    console.error('Unhandled render error:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        className="mx-auto flex min-h-[60vh] max-w-[560px] flex-col items-center justify-center px-5 text-center"
      >
        <p className="font-hand text-2xl text-coral">Well, that is embarrassing</p>
        <h1 className="mt-2 font-display text-[clamp(28px,4vw,42px)] font-extrabold leading-tight text-forest">
          Something on this page broke
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-ink-soft">
          The rest of the site is fine. Try again, and if it keeps happening, please call
          us on{' '}
          <a className="font-bold text-forest underline" href="tel:08001142255">
            0800 114 2255
          </a>
          .
        </p>

        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={this.handleReset}
            className="cursor-pointer rounded-full bg-forest px-6 py-3 font-display text-[17px] font-bold text-white shadow-[0_5px_0_var(--color-forest-deep)] transition hover:translate-y-[3px] hover:shadow-[0_2px_0_var(--color-forest-deep)]"
          >
            Try again
          </button>
          <a
            href="/"
            className="cursor-pointer rounded-full border-2 border-forest px-[22px] py-[11px] font-display text-[17px] font-bold text-forest transition hover:bg-sun"
          >
            Back to the homepage
          </a>
        </div>
      </div>
    );
  }
}
